'use client';
import React, { useState } from 'react';
import { Icon } from '@benzinga/core-ui';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons/faPlusCircle';
import { LineChart, Line, XAxis, YAxis, ReferenceLine, Tooltip } from 'recharts';
import styled from '@benzinga/themetron';
import { MortgageRateTrendChartDataPoint } from '@benzinga/mortgage-rates-manager';
import { isMobile } from '@benzinga/device-utils';

export interface MortgageLoanCompareProps {
  data: MortgageRateTrendChartDataPoint[];
}

export const MortgageLoanCompare: React.FC<MortgageLoanCompareProps> = ({ data }) => {
  const defaultData = data.filter(function (r) {
    r['date'] = new Date(r.M + '/' + r.D + '/' + r.Y).toLocaleString('default', { month: 'short' }) + ' ' + r.D;
    return r;
  });

  const loansNew = defaultData;
  const [isActive, setActive] = useState(false);
  const [tempLoanType, setTempLoanType] = useState(['A51', 'A71', 'R30F']);
  const formatter = (value: number) => `${value.toFixed(2)}%`;
  const loanTypes = {
    A51: { code: 'A51', color: '#10b981', name: '5/1 ARM', styleclass: 'bz_green' },
    A71: { code: 'A71', color: '#057a55', name: '7/1 ARM', styleclass: 'bz_dark_green' },
    R10: { code: 'R10', color: '#0157ff', name: '10 Year Fixed', styleclass: 'bz_light_blue' },
    R15: { code: 'R15', color: '#f59e0b', name: '15 Year Fixed', styleclass: 'bz_dark_yellow' },
    R20: { code: 'R20', color: '#3b5998', name: '20 Year Fixed', styleclass: 'bz_blue' },
    R30: { code: 'R30', color: '#551a8b', name: '30 Year Fixed', styleclass: 'bz_purple' },
    R30F: { code: 'R30F', color: '#ff4050', name: '30 Year Fixed FHA', styleclass: 'bz_red' },
    VA30: { code: 'VA30', color: '#ff7b00', name: '30 Year Fixed VA', styleclass: 'bz_orange' },
  };

  // for dropdown list
  const listLoansType = () => {
    const list: JSX.Element[] = [];
    for (const key in loanTypes) {
      if (!tempLoanType.includes(key)) {
        const item = loanTypes[key];
        list.push(
          <li
            className="bz-compare-select-item text-sm py-1 px-2 border-b cursor-pointer hover:bg-blue-100"
            key={key}
            onClick={() => getHistoricalData(key)}
          >
            {item.name}
          </li>,
        );
      }
    }
    return list;
  };

  // Default 3 loans type
  const defaultLoansBar = () => {
    const defaultBar: JSX.Element[] = [];
    for (let i = 0; i < tempLoanType.length; i++) {
      const label = tempLoanType[i];
      const loan = loanTypes[label];
      const todayData = loansNew[loansNew.length - 1];

      defaultBar.push(
        <div
          className={`bz-loan-compare-item py-2 px-3 border border-gray-200 bg-gray-100 rounded flex mb-2 flex justify-between`}
          key={i}
        >
          <div className="w-full">
            <div className="flex items-center">
              <div className={`bz-loan-compare-status w-4 h-4 ${loan.styleclass} rounded-full`}></div>
              <div className="text-sm font-bold font-manrope ml-2">{loan.name}</div>
            </div>
          </div>
          <div className="ml-2">
            <div className="flex items-center justify-end">
              <div className="text-md font-bold font-manrope mr-4">{todayData[label] + '%'}</div>
              <div className="">
                <button className="flex items-center text-gray-400 text-lg" onClick={() => removeLoan(loan.code)}>
                  <Icon icon={faTimes} />
                </button>
              </div>
            </div>
          </div>
        </div>,
      );
    }
    return defaultBar;
  };

  // Remove loans from chart and bar
  const removeLoan = (loanType: string) => {
    if (tempLoanType.includes(loanType)) {
      const tempLoans: string[] = [];
      for (let i = 0; i < tempLoanType.length; i++) {
        const label = tempLoanType[i];
        const loan = loanTypes[label];
        if (loan.code !== loanType) {
          tempLoans.push(label);
        }
      }
      setTempLoanType(tempLoans);
      setActive(false);
    }
  };

  // Add loans from chart and bar
  const getHistoricalData = (loanType: string) => {
    setActive(!isActive);
    if (!tempLoanType.includes(loanType)) {
      tempLoanType.push(loanType);
      setTempLoanType(tempLoanType);
    }
  };

  // Check loans dropdown is open or not
  const openLoanTypes = () => {
    setActive(!isActive);
  };

  // Add loan in chart
  const loadLines = () => {
    const rows: any[] = [];
    for (let i = 0; i < tempLoanType.length; i++) {
      if (!['D', 'M', 'Y'].includes(tempLoanType[i])) {
        const loan = loanTypes[tempLoanType[i]];
        rows.push(<Line dataKey={tempLoanType[i]} dot={false} key={i} stroke={loan.color} type="linear" />);
      }
    }
    return rows;
  };

  return (
    <MortgageLoanCompareContainer className="loan-compare-container">
      <div className="gap-4 grid grid-cols-3">
        <div className="mortgage-compare-chart col-span-2">
          <LineChart
            data={loansNew}
            height={400}
            margin={{ bottom: 5, left: 0, right: 0, top: 5 }}
            width={isMobile() ? 320 : 600}
          >
            <ReferenceLine stroke="#ddd" y={2} />
            <ReferenceLine stroke="#ddd" y={3} />
            <ReferenceLine stroke="#ddd" y={4} />
            <ReferenceLine stroke="#ddd" y={5} />
            <ReferenceLine stroke="#ddd" y={6} />
            {loadLines()}
            <XAxis axisLine={false} dataKey="date" dy={15} />
            <YAxis axisLine={false} domain={[2, 6]} dx={-15} dy={0} tickFormatter={formatter} />
            <Tooltip />
          </LineChart>
        </div>
        <div className="mortgage-compare-items" col-span-1>
          <div className="bz-loan-compare-items mt-4">{defaultLoansBar()}</div>
          <div className="mt-4">
            <button
              className="bz-loan-compare-btn border border-blue-500 rounded flex items-center text-blue-500 text-sm py-1.5 px-3 font-bold font-manrope"
              onClick={openLoanTypes}
            >
              Add loan type to compare{' '}
              <span className="flex items-center ml-3 font-medium">
                {' '}
                <Icon icon={faPlusCircle} />{' '}
              </span>
            </button>
            <div className={isActive ? `block absolute w-full z-50` : `hidden`}>
              <div className="bz-loan-compare-select-loan">
                <ul className="m-0 p-0 p-2 bg-white rounded-md shadow-md max-w-xs">{listLoansType()}</ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MortgageLoanCompareContainer>
  );
};

const MortgageLoanCompareContainer = styled.div`
  @media (max-width: 800px) {
    .mortgage-compare-chart {
      grid-column: span 3 / span 3;
    }
    .mortgage-compare-items {
      grid-column: span 3 / span 3;
    }
  }
  .bz-loan-compare-btn {
    background: rgba(47, 128, 237, 0.1);
  }
  .date-span {
    color: #283d59;
  }
  .bz-loan-compare-item {
    background: ${({ theme }) => theme.colorPalette.gray50};

    &.blue {
      .bz-loan-compare-status {
        background: #1a79ff;
      }
    }
    &.red {
      .bz-loan-compare-status {
        background: #ff4050;
      }
    }
    &.orange {
      .bz-loan-compare-status {
        background: #f57f36;
      }
    }
  }
  .loan-compare-filter {
    margin-right: -8px;
    margin-left: -8px;
    select {
      color: #5b7292;
      font-weight: 700;
      font-size: 14px;
      padding: 8px 12px;
      border-radius: 8px;
      border: 2px solid #deebfd;
      background-color: transparent;
    }
  }
  .bz_green {
    background-color: #10b981;
  }
  .bz_dark_green {
    background-color: #057a55;
  }
  .bz_red {
    background-color: #ff4050;
  }
  .bz_dark_yellow {
    background-color: #f59e0b;
  }
  .bz_orange {
    background-color: #ff7b00;
  }
  .bz_light_blue {
    background-color: #0157ff;
  }
  .bz_blue {
    background-color: #3b5998;
  }
  .bz_purple {
    background-color: #551a8b;
  }
  .bz-loan-list-wrapper {
    width: fit-content;
  }
  .mortgage-compare-chart {
    .recharts-wrapper {
      font-size: 12px;
      width: 100% !important;
      .recharts-surface {
        width: 100% !important;
        [type='number'] {
          font-size: 12px;
        }
      }
    }
  }
`;
